import type { InstrumentId } from "@trader/shared/MarketData"
import type { AccountId, Side } from "@trader/shared/Money"
import { AccountTopic } from "@trader/shared/topics/Account"
import { useSyncExternalStore } from "react"
import { frameBatcher, subscribeTopic } from "../../live.ts"
import { type AccountState, createAccountStore } from "./accountStore.ts"
import { type AccountsState, createAccountsStore, followedAccountId, type PreferenceStorage } from "./accounts.ts"
import { tradingApi } from "./tradingApi.ts"

// The app's paper-trading data: the account list, the followed account's live
// state (account topic + history pages), and small UI preferences. Components
// and chart controllers read it through the hooks and `subscribe` below.

const storage: PreferenceStorage = {
  get: (key) => {
    try {
      return localStorage.getItem(key)
    } catch {
      return null
    }
  },
  set: (key, value) => {
    try {
      localStorage.setItem(key, value)
    } catch {
      // Not remembered; everything still works.
    }
  }
}

export const accountsStore = createAccountsStore(
  { list: tradingApi.list, create: tradingApi.create, reset: tradingApi.reset, end: tradingApi.end, rename: tradingApi.rename, remove: tradingApi.remove },
  storage
)

export const accountStore = createAccountStore(
  {
    subscribe: (accountId, handlers) => subscribeTopic(AccountTopic, { _tag: "Account", accountId }, handlers),
    loadOrders: tradingApi.listClosedOrders,
    loadFills: tradingApi.listFills
  },
  frameBatcher
)

// The live store follows whichever account the list selects, unless it is archived
// (an ended session has no topic; its summary is read over HTTP).
accountsStore.subscribe(() => accountStore.select(followedAccountId(accountsStore.getState())))

// An account the server refuses (gone, or not this user's) is dropped from the selection;
// one reset elsewhere (another tab) reloads the list, which selects its replacement.
let reloadedFor: string | null = null
accountStore.subscribe(() => {
  const state = accountStore.getState()
  if (state === null) return
  if (state.phase === "failed" && (state.error === "NotFound" || state.error === "Forbidden")) accountsStore.forget(state.accountId)
  if (state.summary?.status === "Archived" && reloadedFor !== state.accountId) {
    reloadedFor = state.accountId
    accountsStore.reload().catch(() => undefined)
  }
})

export const useAccounts = (): AccountsState => useSyncExternalStore(accountsStore.subscribe, accountsStore.getState)

/** The followed account's state; re-renders at most once per frame. */
export const useAccountState = (): AccountState | null => useSyncExternalStore(accountStore.subscribe, accountStore.getState)

/** One field of the followed account (stable until it changes), so a component re-renders only for it. */
export const useAccountField = <K extends keyof AccountState>(key: K): AccountState[K] | null =>
  useSyncExternalStore(accountStore.subscribe, () => accountStore.getState()?.[key] ?? null)

const NONE: ReadonlyArray<never> = []

// Selector hooks: each table re-renders only when its own list changes.
export const useAccountSummary = () => useAccountField("summary")
export const usePositions = () => useAccountField("positions") ?? NONE
export const useOpenOrders = () => useAccountField("openOrders") ?? NONE
export const useOrderHistory = () => useAccountField("orderHistory") ?? NONE
export const useFills = () => useAccountField("fills") ?? NONE
export const useRunners = () => useAccountField("runners") ?? NONE

export const selectedAccountId = (): AccountId | null => accountsStore.getState().selectedId

// --- Small preferences (this browser) ------------------------------------------------------

const preference = <T extends string>(key: string, allowed: ReadonlyArray<T>, fallback: T) => {
  const loaded = storage.get(key)
  let value: T = allowed.find((option) => option === loaded) ?? fallback
  const listeners = new Set<() => void>()
  return {
    get: (): T => value,
    set: (next: T): void => {
      if (next === value) return
      value = next
      storage.set(key, next)
      for (const listener of listeners) listener()
    },
    subscribe: (listener: () => void) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    }
  }
}

/** Clip charts at a replay account's virtual time (no future bars). On by default. */
export const syncChartPreference = preference("trader.trading.syncChart", ["on", "off"], "on")
export const useSyncChart = (): boolean =>
  useSyncExternalStore(syncChartPreference.subscribe, syncChartPreference.get) === "on"

export type TradingTab = "positions" | "orders" | "fills" | "strategies" | "account" | "summary"
export const TRADING_TABS: ReadonlyArray<TradingTab> = ["positions", "orders", "fills", "strategies", "account", "summary"]
export const tabPreference = preference<TradingTab>("trader.trading.tab", TRADING_TABS, "positions")
export const useTradingTab = (): TradingTab => useSyncExternalStore(tabPreference.subscribe, tabPreference.get)

/** TradingView's Buy / Sell buttons on the charts' top-left. On by default; the chart's right-click menu hides them. */
export const quickTradePreference = preference("trader.chart.quickTrade", ["on", "off"], "on")
export const useQuickTrade = (): boolean => useSyncExternalStore(quickTradePreference.subscribe, quickTradePreference.get) === "on"

/** Whether the Trading panel is collapsed (just its header). */
export const collapsedPreference = preference("trader.trading.collapsed", ["true", "false"], "false")
export const useTradingCollapsed = (): boolean =>
  useSyncExternalStore(collapsedPreference.subscribe, collapsedPreference.get) === "true"

// --- The order ticket, from anywhere ------------------------------------------------------

const qtyListeners = new Set<() => void>()

/**
 * The quantity for an instrument (this browser): set by the chart's Buy / Sell box or the last
 * ticket placed, and used by the ticket, the chart's right-click orders and Buy / Sell.
 */
export const lastTicketQty = {
  get: (instrumentId: InstrumentId): string | null => storage.get(`trader.trading.qty.${instrumentId}`),
  set: (instrumentId: InstrumentId, qty: string): void => {
    if (storage.get(`trader.trading.qty.${instrumentId}`) === qty) return
    storage.set(`trader.trading.qty.${instrumentId}`, qty)
    for (const listener of qtyListeners) listener()
  },
  subscribe: (listener: () => void) => {
    qtyListeners.add(listener)
    return () => {
      qtyListeners.delete(listener)
    }
  }
}

/** An instrument's quantity as a hook (null: none set yet). */
export const useLastTicketQty = (instrumentId: InstrumentId): string | null =>
  useSyncExternalStore(lastTicketQty.subscribe, () => lastTicketQty.get(instrumentId))

/** A ticket to open, filled in (the chart's "Create new order…" at a price). */
export interface TicketRequest {
  readonly instrumentId: InstrumentId
  readonly side: Side
  readonly type: "Market" | "Limit" | "Stop"
  /** The limit or stop price, on the tick grid; null for the ticket's default. */
  readonly price: string | null
}

const ticketListeners = new Set<(request: TicketRequest) => void>()

/** The Trading panel opens the ticket; a chart asks it to. */
export const ticketRequests = {
  open: (request: TicketRequest): void => {
    for (const listener of ticketListeners) listener(request)
  },
  subscribe: (listener: (request: TicketRequest) => void) => {
    ticketListeners.add(listener)
    return () => {
      ticketListeners.delete(listener)
    }
  }
}

// --- Last prices, from the charts ----------------------------------------------------------

// The ticket defaults its limit and stop prices to the last bar a chart shows
// of the instrument (clipped at the replay time when the chart follows a replay).
const lastCloses = new Map<InstrumentId, number>()
const quoteListeners = new Set<() => void>()

export const chartQuotes = {
  report: (instrumentId: InstrumentId, close: number | undefined): void => {
    if (close === undefined || !Number.isFinite(close) || lastCloses.get(instrumentId) === close) return
    lastCloses.set(instrumentId, close)
    for (const listener of quoteListeners) listener()
  },
  last: (instrumentId: InstrumentId): number | null => lastCloses.get(instrumentId) ?? null,
  subscribe: (listener: () => void) => {
    quoteListeners.add(listener)
    return () => {
      quoteListeners.delete(listener)
    }
  }
}

/** The last price a chart shows of an instrument, as a hook (the Buy / Sell buttons). */
export const useLastPrice = (instrumentId: InstrumentId): number | null =>
  useSyncExternalStore(chartQuotes.subscribe, () => chartQuotes.last(instrumentId))
